export interface Lesson {
  title: string
  summary?: string
  duration?: string
  preview?: boolean
}

export interface Module {
  title: string
  lessons: Lesson[]
}

export interface OutcomeJob {
  role: string
  where: string
  salary: string
  tip: string
}

export interface OutcomeFreelance {
  platform: string
  gig: string
  earning: string
  tip: string
}

export interface OutcomeProject {
  name: string
  client: string
  value: string
  description: string
}

export interface CourseOutcomes {
  firstMilestone: string
  jobs: OutcomeJob[]
  freelance: OutcomeFreelance[]
  projects: OutcomeProject[]
}

export interface Course {
  slug: string
  title: string
  subtitle: string
  description: string
  instructor: string
  supportInstructor?: string
  level: 'Beginner' | 'Intermediate' | 'Advanced'
  category: string
  tags: string[]
  language: string
  duration: string
  schedule: string
  classDuration?: string
  batchSize: string
  price: string
  discountedPrice?: string
  earlyBirdPrice?: string
  promoPriceLabel?: string
  priceRationale?: {
    heading: string
    summary: string
    points: string[]
  }
  status: 'open' | 'coming-soon'
  classOpening?: 'teacher'
  flagship?: boolean
  priority: number
  targetStudents: string
  prerequisites: string[]
  tools: string[]
  highlights: string[]
  pros: string[]
  cons: string[]
  careerPaths: string[]
  outcomes?: CourseOutcomes
  curriculum: Module[]
}

export const courseCategories = [
  'All',
  'AI & Automation',
  'Web Development',
  'Mobile',
  'Design',
  'Marketing',
  'Foundation',
] as const

export const courseLevels = ['All', 'Beginner', 'Intermediate', 'Advanced'] as const

export const courses: Course[] = [
  {
    slug: 'ai-agent-builder-beginner-to-pro',
    title: 'AI Agent Builder: From Beginner to Pro',
    subtitle: 'Learn Practical AI Agent Skills for Studies, Projects & Career Growth',
    description:
      'The flagship skill-building course of Skillo. Learn to design, build, and deploy AI agents and automation workflows using industry-relevant tools — from no-code platforms like n8n to LangChain-powered multi-agent systems. Students gain practical AI engineering skills they can apply to university projects, personal experiments, employment, startups, or freelancing — depending on their goals, practice, and effort. This is a skills course, not an income or job guarantee.',
    instructor: 'Minhaj Sadik',
    level: 'Beginner',
    category: 'AI & Automation',
    tags: ['AI Agents', 'n8n', 'LangChain', 'Automation', 'OpenAI', 'RAG'],
    language: 'Bangla + English',
    duration: '12 weeks',
    schedule: '3 classes/week × 2 hours',
    batchSize: 'Max 20 students',
    price: '৳14,999',
    earlyBirdPrice: '৳11,999',
    status: 'open',
    flagship: true,
    priority: 1,
    targetStudents:
      'University students, CS graduates, junior developers, career switchers, and curious beginners aged 18–35 who want practical AI agent and automation skills for studies, projects, and long-term career advantage.',
    prerequisites: [
      'Basic computer literacy',
      'Comfort with English documentation',
      'ChatGPT familiarity',
    ],
    tools: [
      'n8n',
      'Make.com',
      'Zapier',
      'OpenAI / Claude / Gemini APIs',
      'LangChain / LangGraph',
      'Pinecone / Supabase (vector DB)',
      'Airtable & Google Sheets',
      'WhatsApp / Telegram integrations',
      'Retell AI / ElevenLabs (voice)',
      'Cursor & Lovable',
    ],
    highlights: [
      '12-week structured program with live classes',
      'Build real AI agents for real business use cases',
      'No-code to pro-code: n8n → LangChain',
      'RAG pipelines with vector databases',
      'WhatsApp & voice AI agent deployment',
      'Portfolio demos, documentation & presentation skills',
      'Capstone: ship a portfolio-ready automation project',
      'Certificate of completion',
    ],
    pros: [
      'কোনো কোডিং জানতে হবে না — n8n-এর ভিজ্যুয়াল ওয়ার্কফ্লো একদম নতুনদের জন্য তৈরি',
      'শিক্ষার্থী জীবনে এগিয়ে থাকার সুযোগ — প্রজেক্ট, অ্যাসাইনমেন্ট ও ইন্টারভিউতে দেখানোর মতো প্র্যাকটিক্যাল AI স্কিল',
      'ইন্ডাস্ট্রি-রিলেভেন্ট টুলস: n8n, APIs, RAG, WhatsApp agents, multi-agent concepts',
      'একই স্কিল পরে চাকরি, স্টার্টআপ, পার্সোনাল প্রজেক্ট বা ফ্রিল্যান্সিংয়ে প্রয়োগ করা যায় — পথটি শিক্ষার্থীর লক্ষ্য ও প্রচেষ্টার উপর নির্ভর করে',
      'পোর্টফোলিও-ফোকাসড ক্যাপস্টোন — কাজ করে এমন ডেমো ও কেস স্টাডি তৈরির অনুশীলন',
      'আরও এগিয়ে যেতে চাইলে সরাসরি Agentic Python কোর্সে যাওয়ার সুযোগ আছে',
      'Flagship কোর্স — সবচেয়ে বেশি ইন্সট্রাক্টর মনোযোগ ও কমিউনিটি সাপোর্ট',
    ],
    cons: [
      'শেখার সময় OpenAI/Claude API-তে প্রতি মাসে ~$৫–১৫ খরচ হবে — ছোট চলমান বাজেট রাখতে হবে',
      'স্কিলো কোনো চাকরি, ক্লায়েন্ট বা আয়ের গ্যারান্টি দেয় না — সাফল্য নির্ভর করে অনুশীলন, পোর্টফোলিও, যোগাযোগ ও বাজারের চাহিদার উপর',
      'ফ্রিল্যান্সিং একটি সম্ভাব্য পথ মাত্র — এটি কোর্সের মূল প্রতিশ্রুতি নয়; আলাদা প্রচেষ্টা ও ধৈর্য লাগে',
      'ঐতিহ্যগত "সফটওয়্যার ডেভেলপার" পদ নয় — স্থানীয় চাকরির বাজারে এই রোল এখনও গড়ে উঠছে',
    ],
    careerPaths: [
      'Stronger student projects, research demos & academic advantage',
      'Junior AI / automation roles at startups and IT firms (competitive)',
      'Optional freelancing path — if you build a portfolio and pursue clients',
      'Startup experiments, personal products & SME automation projects',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a portfolio-ready WhatsApp booking bot for a restaurant or clinic use case — a working demo you can present for class projects, interviews, personal experiments, or future opportunities. Outcomes after that depend on your practice and goals.',
      jobs: [
        {
          role: 'AI Automation / Workflow Associate',
          where: 'IT firms, SaaS startups, and ops teams exploring automation',
          salary: 'Market-dependent',
          tip: 'Treat this as a competitive path, not a placement promise. A clear demo video, documented workflow, and consistent practice matter more than a certificate alone.',
        },
        {
          role: 'AI Implementation / Solutions Support',
          where: 'Agencies and product teams that integrate AI tools for clients',
          salary: 'Role & experience dependent',
          tip: 'Publish one public walkthrough of a real business use case. Hiring outcomes vary widely by English, communication, location, and experience.',
        },
        {
          role: 'In-House Automation Support',
          where: 'Growing local businesses and e-commerce operations teams',
          salary: 'Varies by company',
          tip: 'Internal roles often value reliability and process understanding. Use your capstone to show how you solve a specific operational problem — not as an earnings claim.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'n8n / Make.com automation workflow setup',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise. Profiles, proposals, reviews, English, and market demand all affect results.',
        },
        {
          platform: 'Fiverr (optional path)',
          gig: 'AI chatbot for WhatsApp or Telegram',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'A clear 60-second demo helps people understand your skill. Getting paid work still requires consistent outreach, delivery quality, and patience.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Business automation demos for restaurants, clinics & shops',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Local conversations can be a learning opportunity. Do not promise businesses income or magical AI results — show a tested demo and set honest expectations.',
        },
      ],
      projects: [
        {
          name: 'Restaurant WhatsApp Booking Bot',
          client: 'Portfolio use case: restaurants and cafes',
          value: 'Capstone / portfolio project',
          description:
            'Practice building a workflow that handles reservation requests, menu FAQs, and confirmation-style messages via WhatsApp-style automation patterns in n8n.',
        },
        {
          name: 'Clinic Appointment AI Agent',
          client: 'Portfolio use case: clinics and diagnostic centers',
          value: 'Capstone / portfolio project',
          description:
            'Practice booking-style flows, reminders, and FAQ handling so you understand real operational constraints — useful for demos, class projects, and interviews.',
        },
        {
          name: 'Real Estate Lead Qualification Bot',
          client: 'Portfolio use case: property agencies',
          value: 'Capstone / portfolio project',
          description:
            'Practice capturing leads, qualifying interest through conversation logic, and routing high-intent prospects — a strong systems-thinking portfolio piece.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Week 1: AI & LLM Fundamentals',
        lessons: [
          { title: 'ChatGPT, Claude, Gemini, DeepSeek — compared', preview: true },
          { title: 'Prompt engineering fundamentals', preview: true },
          { title: 'OpenAI API setup & cost economics' },
          { title: 'Understanding tokens, context windows & pricing' },
        ],
      },
      {
        title: 'Week 2: APIs, Webhooks & JSON',
        lessons: [
          { title: 'REST APIs — the language of automation' },
          { title: 'Webhooks & event-driven patterns' },
          { title: 'JSON data structures & manipulation' },
          { title: 'Hands-on API testing with Postman' },
        ],
      },
      {
        title: 'Week 3: n8n Core Workflows',
        lessons: [
          { title: 'Triggers, nodes & expressions' },
          { title: 'Gmail → Google Sheets automation' },
          { title: 'RSS → Telegram bot' },
          { title: 'Form → Airtable pipeline' },
        ],
      },
      {
        title: 'Week 4: Function Calling & Tool Use',
        lessons: [
          { title: 'Function calling deep dive' },
          { title: 'Building your first single-tool AI agent' },
          { title: 'Structured output with JSON mode' },
          { title: 'Error handling in AI workflows' },
        ],
      },
      {
        title: 'Week 5: Multi-Tool AI Agents',
        lessons: [
          { title: 'Calendar, Gmail, Sheets & Web Search agents' },
          { title: 'Memory: window buffer + long-term (Supabase)' },
          { title: 'Chaining multiple tools in a single agent' },
          { title: 'Testing & debugging agent workflows' },
        ],
      },
      {
        title: 'Week 6: RAG — Retrieval-Augmented Generation',
        lessons: [
          { title: 'Embeddings & vector databases (Pinecone)' },
          { title: '"Chat with your docs" bot — end to end' },
          { title: 'Chunking strategies & retrieval quality' },
          { title: 'Hybrid search & reranking' },
        ],
      },
      {
        title: 'Week 7: WhatsApp & Voice AI Agents',
        lessons: [
          { title: 'WhatsApp Business API integration' },
          { title: 'Telegram AI agent deployment' },
          { title: 'Voice agents with Retell AI / ElevenLabs' },
          { title: 'Multi-channel bot architecture' },
        ],
      },
      {
        title: 'Week 8: Multi-Agent Systems',
        lessons: [
          { title: 'LangGraph "Think" node & agent teams' },
          { title: 'Supervisor & worker agent patterns' },
          { title: 'Model Context Protocol (MCP) intro' },
          { title: 'Debugging multi-agent workflows' },
        ],
      },
      {
        title: 'Week 9: Real-World Business Agents',
        lessons: [
          { title: 'Lead-generation bot for businesses' },
          { title: 'Customer-support AI agent' },
          { title: 'Content repurposing automation' },
          { title: 'Data extraction & reporting agents' },
        ],
      },
      {
        title: 'Week 10: Packaging Your Skills Professionally',
        lessons: [
          { title: 'How automation work is typically scoped & documented' },
          { title: 'Service package templates & handoff notes (optional path)' },
          { title: 'Reliability, support expectations & ethical delivery' },
          { title: 'Self-hosting basics with n8n for learning & demos' },
        ],
      },
      {
        title: 'Week 11: Opportunity Channels & Communication',
        lessons: [
          { title: 'Presenting your work: demos, case studies & LinkedIn' },
          { title: 'Optional freelancing profile basics (no income promise)' },
          { title: 'Clear proposals, honest scoping & professional communication' },
          { title: 'Personal site / portfolio page for your projects' },
        ],
      },
      {
        title: 'Week 12: Capstone Project',
        lessons: [
          { title: 'Ship a portfolio-ready business automation project' },
          { title: 'Document 3 case studies with demos and learnings' },
          { title: 'Final presentation & peer review' },
          { title: 'Certificate ceremony & realistic next-step guidance' },
        ],
      },
    ],
  },

  {
    slug: 'full-stack-ai-web-development',
    title: 'Full Stack Web Development with AI Integration',
    subtitle: 'From HTML to Production — with AI superpowers',
    description:
      'A 24-week live program of 72 classes that takes beginners from zero, how the web works and a first HTML page, to building, testing and deploying full-stack web applications with AI features. Learn Git, HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, Express, PostgreSQL, MongoDB, authentication, security and payments, with homework after every class, a check at the end of every module and a final assessment. Students can apply these skills to studies, employment, startups, or optional freelancing depending on practice and goals. This is a skills course, not an income or job guarantee.',
    instructor: 'Minhaj Sadik',
    level: 'Beginner',
    category: 'Web Development',
    tags: ['React', 'Next.js', 'Node.js', 'TypeScript', 'PostgreSQL', 'MongoDB'],
    language: 'Bangla + English',
    duration: '24 weeks',
    schedule: '3 classes/week × 2 hours',
    classDuration: '2 hours',
    batchSize: 'Max 20 students',
    price: '৳14,999',
    earlyBirdPrice: '৳11,999',
    priceRationale: {
      heading: 'Why this flagship course costs ৳11,999',
      summary:
        'This is priced as a 24-week live cohort, not a recorded web tutorial bundle. Students pay for mentor-led classes, project feedback, and a portfolio path from HTML to a deployed AI-powered app.',
      points: [
        '144 hours of live class time across 24 weeks (72 classes, 3 a week × 2 hours), with a maximum of 20 students in the main batch.',
        'A full beginner-to-production stack: Git, HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, Express, PostgreSQL, MongoDB, testing, security, auth, payments, deployment, and OpenAI API integration.',
        'Multiple portfolio projects with review, including a deployed capstone — not a certificate-only program.',
        'A lower early-bird fee than the regular ৳14,999 price, saving students ৳3,000.',
      ],
    },
    status: 'open',
    classOpening: 'teacher',
    flagship: true,
    priority: 2,
    targetStudents:
      'College students, fresh CS/CSE graduates, and self-taught coders who want to become professional full-stack developers.',
    prerequisites: [
      'Basic computer skills: typing, files and folders, and using a web browser (the Basic Computer & English course covers them)',
      'Willingness to code 10+ hours/week',
      'Own laptop with internet access',
    ],
    tools: [
      'Git / GitHub',
      'HTML / CSS / Tailwind CSS',
      'JavaScript / TypeScript',
      'React 19 / Next.js',
      'Node.js / Express',
      'PostgreSQL / Prisma',
      'MongoDB / Mongoose',
      'Vitest / Testing Library',
      'Vercel / Docker / GitHub Actions',
      'OpenAI API',
      'SSLCommerz / bKash / Stripe',
      'VS Code / Cursor',
    ],
    highlights: [
      '24-week structured program with 72 live classes',
      'HTML from zero in week one, then Git, GitHub and CSS',
      'Modern stack: React 19, Next.js, TypeScript',
      'SQL and NoSQL: PostgreSQL with Prisma, MongoDB with Mongoose',
      'Testing, web security, authentication & payments',
      'AI features with the OpenAI API',
      'Homework after every class, a check after every module and a final assessment',
      'Deployed capstone, portfolio & certificate of completion',
    ],
    pros: [
      'সবচেয়ে বেশি ক্যারিয়ার অপশন: ফ্রিল্যান্সিং + স্থানীয় IT চাকরি + SaaS ফাউন্ডার — সবচেয়ে বহুমুখী কোর্স',
      'মৌলিক দক্ষতা যা কখনো পুরনো হয় না — HTML, JavaScript, React এবং API সবসময় চাহিদায় থাকে',
      'বিশাল স্থানীয় নিয়োগ বাজার — ঢাকা ও সিলেটের IT ফার্মগুলো সক্রিয়ভাবে React/Node ডেভেলপার নিচ্ছে',
      'গ্র্যাজুয়েশনের সময় ৫টির বেশি রিয়েল প্রজেক্ট — চাকরির আবেদনের জন্য শক্তিশালী পোর্টফোলিও',
      'Mobile App Development এবং Agentic Python কোর্সে যাওয়ার স্বাভাবিক পথ — আরও এগোনোর সুযোগ',
    ],
    cons: [
      'আন্তর্জাতিক ফ্রিল্যান্স প্ল্যাটফর্মে প্রতিযোগিতা বেশি — হাজার হাজার বাংলাদেশি ডেভেলপার ইতোমধ্যে সেখানে আছে',
      'এন্ট্রি লেভেলে বাজার স্যাচুরেটেড হওয়ায় আন্তর্জাতিকভাবে শুরুর রেট কম ($৮–১৫/ঘণ্টা)',
      'AI অটোমেশন স্পেশালিস্টদের তুলনায় প্রথম ফ্রিল্যান্স ক্লায়েন্ট পেতে বেশি সময় লাগে',
      'ভালোভাবে এগোতে প্রতি সপ্তাহে কমপক্ষে ১০+ ঘণ্টা হাতে-কলমে কোডিং প্র্যাকটিস করতে হবে',
    ],
    careerPaths: [
      'Portfolio-ready web projects for studies, interviews, and personal work',
      'Junior web developer roles at local IT firms (competitive, not a placement promise)',
      'Optional freelancing path if you build a public portfolio and pursue clients',
      'SaaS experiments, personal products, and small-business websites',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a portfolio-ready Next.js + Node project — for example a small business site or dashboard — that you can present for class, interviews, or personal experiments. Outcomes after that depend on your practice and goals.',
      jobs: [
        {
          role: 'Junior Frontend Developer (React)',
          where: 'IT firms in Dhaka and Sylhet',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path, not a placement promise. A live GitHub portfolio and one deployed demo matter more than a certificate alone.',
        },
        {
          role: 'Junior Full Stack Developer',
          where: 'Remote and local product teams',
          salary: 'Role & experience dependent',
          tip: 'Keep one polished Next.js + AI project live. Hiring outcomes vary widely by English, communication, location, and experience.',
        },
        {
          role: 'Web Application Developer',
          where: 'Agencies building client projects',
          salary: 'Varies by company',
          tip: 'Agencies often value reliability and a clear demo. Use your capstone to show a specific problem you solved — not as an earnings claim.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'React and Next.js web application development',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise. Profiles, proposals, reviews, English, and market demand all affect results.',
        },
        {
          platform: 'Fiverr (optional path)',
          gig: 'Responsive business website (design → code)',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'A clear live demo helps people understand your skill. Getting paid work still requires consistent outreach, delivery quality, and patience.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Small-business websites and simple booking flows',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Local conversations can be a learning opportunity. Do not promise businesses sales or traffic — show a tested demo and set honest expectations.',
        },
      ],
      projects: [
        {
          name: 'AI-Powered SaaS Dashboard',
          client: 'Portfolio use case: startups and small teams',
          value: 'Capstone / portfolio project',
          description:
            'Practice building a full-stack Next.js application with user authentication, dashboard views, and an embedded AI assistant pattern — deployable as a public demo.',
        },
        {
          name: 'E-Commerce Site with bKash & SSLCommerz',
          client: 'Portfolio use case: local retailers',
          value: 'Capstone / portfolio project',
          description:
            'Practice a React and Node.js shop with product catalog, cart, payment-gateway integration, order management, and an admin panel.',
        },
        {
          name: 'Doctor Appointment Booking Platform',
          client: 'Portfolio use case: clinics and practices',
          value: 'Capstone / portfolio project',
          description:
            'Practice a multi-doctor scheduling flow with a patient portal, reminder-style messaging patterns, admin dashboard, and payment integration.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Week 1: Introduction to web development and HTML',
        lessons: [
          {
            title: 'Introduction to web development',
            summary:
              'What web development is, how the web works, frontend, backend and full stack, the roles of HTML, CSS and JavaScript, setting up VS Code and Chrome, and your first web page.',
            preview: true,
          },
          {
            title: 'HTML fundamentals and structure',
            summary:
              'Tags, elements and attributes, comments and void elements, nesting and the element tree, block and inline elements, div and span, file paths, internal and external links, clean HTML and debugging in the browser.',
            preview: true,
          },
          {
            title: 'Text formatting, lists and tables',
            summary:
              'Headings and whitespace, text-level elements, quotations, HTML entities and the taka sign, ordered, unordered and description lists, and accessible data tables.',
          },
        ],
      },
      {
        title: 'Weeks 2–3: HTML in depth, Git and GitHub',
        lessons: [
          {
            title: 'Images, media and the page head',
            summary:
              'Images in depth with formats, sizes and alt text, figures, audio, video and captions, embedded maps and videos, links in depth, and the head: metadata, favicon and link previews.',
          },
          {
            title: 'Forms, part 1: how forms work, inputs, labels and buttons',
            summary:
              'The form element, action and method, GET and POST, names and values, labels, text inputs, textarea, buttons, fieldset and legend, and a contact form.',
          },
          {
            title: 'Forms, part 2: choices, special inputs and validation',
            summary:
              'Checkboxes, radio buttons, select lists and datalist, date, time, range and file inputs, built-in validation with required, min, max and pattern, and accessible forms.',
          },
          {
            title: 'Semantic HTML and accessibility',
            summary:
              'Semantic page structure with header, nav, main, section, article, aside and footer, landmarks and heading outline, accessible names, keyboard access, ARIA basics, and checking pages with the validator and Lighthouse.',
          },
          {
            title: 'Project: a multi-page website in HTML',
            summary:
              'Plan, build and check the three-page Sylhet Cafe website in HTML with a shared header and footer, a quality checklist and a code review in pairs.',
          },
          {
            title: 'The terminal, Git and GitHub: publish your website',
            summary:
              'Terminal basics, installing and setting up Git and Node.js, the edit, add, commit and push cycle, GitHub, README files in Markdown, and publishing a site with GitHub Pages.',
          },
        ],
      },
      {
        title: 'Weeks 4–5: CSS fundamentals',
        lessons: [
          {
            title: 'CSS basics: rules, selectors, colours and units',
            summary:
              'Linking a stylesheet, rule syntax, type, class, ID and attribute selectors, combinators, colours in hex, rgb and hsl, and px, rem, em and percentage units.',
          },
          {
            title: 'The cascade, specificity, inheritance and DevTools',
            summary:
              'Which rule wins and why, calculating specificity, inheritance, why to avoid !important, and inspecting and editing styles live in DevTools.',
          },
          {
            title: 'The box model: margin, padding, border and display',
            summary:
              'Content, padding, border and margin, box-sizing: border-box, collapsing margins, block, inline and inline-block, width, height, min and max sizes, and overflow.',
          },
          {
            title: 'Typography, web fonts and backgrounds',
            summary:
              'Font stacks and Google Fonts, including Hind Siliguri for Bangla, size, weight, line height and spacing, text alignment, readable line length, backgrounds, gradients and colour contrast.',
          },
          {
            title: 'Positioning and layering',
            summary:
              'Static, relative, absolute, fixed and sticky positioning, offsets, z-index and stacking contexts, a sticky header, a badge on a card, and centring techniques.',
          },
          {
            title: 'Pseudo-classes, pseudo-elements, variables and transitions',
            summary:
              ':hover, :focus-visible and :nth-child, ::before and ::after, custom properties for a colour system, transitions, transforms and keyframe animations that respect reduced motion.',
          },
        ],
      },
      {
        title: 'Weeks 6–7: Layout and responsive design',
        lessons: [
          {
            title: 'Flexbox, part 1: the flex container',
            summary:
              'display: flex, the main and cross axes, flex-direction, justify-content, align-items, gap and flex-wrap, and building a navigation bar.',
          },
          {
            title: 'Flexbox, part 2: flexible items and real components',
            summary:
              'flex-grow, flex-shrink and flex-basis, align-self and order, min-width: 0, and building cards, a media object and a footer that stays at the bottom.',
          },
          {
            title: 'CSS Grid, part 1: rows, columns and fr',
            summary:
              'display: grid, grid-template-columns and grid-template-rows, the fr unit, repeat(), gap, placing items by lines and spans, and a photo gallery.',
          },
          {
            title: 'CSS Grid, part 2: areas and responsive grids',
            summary:
              'grid-template-areas, auto-fit and minmax() for responsive cards, the implicit grid, aligning items in a grid, and choosing between Flexbox and Grid.',
          },
          {
            title: 'Responsive design: mobile-first, media queries and images',
            summary:
              'The viewport meta tag, mobile-first CSS, min-width media queries, fluid sizes with clamp(), responsive images with srcset and picture, and testing in device mode.',
          },
          {
            title: 'Project: a responsive landing page, deployed',
            summary:
              'Plan, build and test a complete landing page for a Sylhet business from a written brief, check it with Lighthouse and the keyboard, and deploy it to GitHub Pages.',
          },
        ],
      },
      {
        title: 'Week 8: Tailwind CSS',
        lessons: [
          {
            title: 'Tailwind CSS: setup and utility classes',
            summary:
              'Why utility-first CSS, installing Tailwind CSS v4 with the CLI, spacing, colour, typography, border and shadow utilities, and reading a component made of utilities.',
          },
          {
            title: 'Tailwind CSS: responsive design, states, dark mode and themes',
            summary:
              'sm, md and lg variants, hover and focus-visible states, dark mode, design tokens with @theme, reusable patterns and when @apply is reasonable.',
          },
          {
            title: 'Project: rebuild the landing page with Tailwind CSS',
            summary:
              'Rebuild the landing page from Week 7 with Tailwind CSS, compare the two stylesheets, build for production and deploy the new version.',
          },
        ],
      },
      {
        title: 'Weeks 9–10: JavaScript fundamentals',
        lessons: [
          {
            title: 'JavaScript basics: variables, values and types',
            summary:
              'Running JavaScript in the browser console and with Node.js, const and let, strings, numbers, booleans, null and undefined, typeof and template literals.',
          },
          {
            title: 'Operators, comparisons and decisions',
            summary:
              'Arithmetic and assignment operators, strict equality, logical operators, truthy and falsy values, if and else, switch and the ternary operator.',
          },
          {
            title: 'Loops and iteration',
            summary:
              'for, while and for...of loops, break and continue, counting, totals and searches with loops, and how to avoid infinite loops.',
          },
          {
            title: 'Functions',
            summary:
              'Function declarations and arrow functions, parameters and defaults, return values, scope basics, pure functions, and splitting a problem into small functions.',
          },
          {
            title: 'Arrays and array methods',
            summary:
              'Creating and changing arrays, map, filter, find, some, every, reduce and sort, chaining methods, and working with lists of data.',
          },
          {
            title: 'Objects and JSON',
            summary:
              'Object literals, properties and methods, destructuring, spread and rest, arrays of objects, Object.keys, values and entries, and JSON.parse and JSON.stringify.',
          },
        ],
      },
      {
        title: 'Weeks 11–12: JavaScript in depth and the DOM',
        lessons: [
          {
            title: 'Scope, closures and this',
            summary:
              'Block and function scope, hoisting basics, closures for private state, this in methods and arrow functions, and common scope bugs.',
          },
          {
            title: 'Classes and object-oriented JavaScript',
            summary:
              'Classes, constructors and methods, getters, private fields, inheritance with extends and super, and when to use a class or a plain object.',
          },
          {
            title: 'Modules, errors and debugging',
            summary:
              'ES modules with import and export, try, catch and finally, throwing useful errors, and debugging with breakpoints in DevTools.',
          },
          {
            title: 'The DOM: selecting, changing and creating elements',
            summary:
              'querySelector and querySelectorAll, textContent and why innerHTML is risky, classList, attributes and dataset, creating elements and rendering a list from data.',
          },
          {
            title: 'Events, forms and saving data in the browser',
            summary:
              'addEventListener and the event object, form submit and FormData, event delegation, localStorage, and building a theme toggle.',
          },
          {
            title: 'Project: an interactive to-do or quiz app',
            summary:
              'Build a complete browser app that renders from state, with filters, editing, validation and data saved in localStorage, then deploy it.',
          },
        ],
      },
      {
        title: 'Week 13: Asynchronous JavaScript and APIs',
        lessons: [
          {
            title: 'Asynchronous JavaScript: the event loop, promises and async/await',
            summary:
              'Why JavaScript runs one thing at a time, timers, the event loop, promises with then and catch, async and await, and running tasks in parallel with Promise.all.',
          },
          {
            title: 'Fetch, JSON and REST APIs',
            summary:
              'HTTP methods in practice, fetch with GET and POST, checking response.ok, loading, error and empty states, CORS, and why API keys never go in the browser.',
          },
          {
            title: 'Project: an app built on a public API',
            summary:
              'Build a weather or country explorer on a public API with search, loading and error states and cached results, then deploy it.',
          },
        ],
      },
      {
        title: 'Week 14: Tooling, TypeScript and teamwork with Git',
        lessons: [
          {
            title: 'npm, Vite, ESLint, Prettier and working in a team with Git',
            summary:
              'package.json and npm scripts, a Vite project, linting and formatting, Git branches, pull requests, code review and resolving merge conflicts.',
          },
          {
            title: 'TypeScript basics',
            summary:
              'Why types help, annotations and inference, object types and interfaces, union and literal types, narrowing, and strict mode.',
          },
          {
            title: 'TypeScript in practice: generics and validation with Zod',
            summary:
              'Generics, the utility types Partial, Pick, Omit and Record, typing functions and API data, and checking data at runtime with Zod.',
          },
        ],
      },
      {
        title: 'Weeks 15–17: React',
        lessons: [
          {
            title: 'React basics: components, JSX and props',
            summary:
              'Creating a React app with Vite, components, the rules of JSX, props and children, and splitting a page into components.',
          },
          {
            title: 'State and events',
            summary:
              'useState, handling events, updating state correctly, state as a snapshot, and lifting state up to a shared parent.',
          },
          {
            title: 'Lists, keys and conditional rendering',
            summary:
              'Rendering arrays with map and keys, showing and hiding parts of the page, empty states, and a filterable list.',
          },
          {
            title: 'Forms in React',
            summary:
              'Controlled inputs, checkboxes and selects, handling submit, validating with Zod and showing errors next to each field.',
          },
          {
            title: 'Effects and data fetching',
            summary:
              'useEffect and its dependencies, cleanup, fetching data with loading and error states, race conditions, and when you do not need an effect.',
          },
          {
            title: 'Custom hooks and component patterns',
            summary:
              'Writing custom hooks, composition with children, refs, sharing logic without sharing state, and keeping components small.',
          },
          {
            title: 'Routing with React Router',
            summary:
              'Routes, layouts and Outlet, links and navigation, URL params, loaders and error pages in a multi-page app.',
          },
          {
            title: 'Context, reducers and server state',
            summary:
              'useReducer, context for shared state, TanStack Query for server data, caching, mutations and invalidation.',
          },
          {
            title: 'Project: a multi-page React app, deployed',
            summary:
              'Build and deploy a restaurant menu app with routing, a cart, forms and data from an API, styled with Tailwind CSS.',
          },
        ],
      },
      {
        title: 'Week 18: Next.js',
        lessons: [
          {
            title: 'Next.js basics: the App Router, pages and layouts',
            summary:
              'Creating a Next.js app, file-based routing, layouts, links, dynamic routes, metadata and optimised images.',
          },
          {
            title: 'Server Components, Client Components and data',
            summary:
              "Rendering on the server, when to use 'use client', fetching data in Server Components, loading and error files, and caching basics.",
          },
          {
            title: 'Server Actions, forms and route handlers',
            summary:
              'Handling forms with Server Actions and useActionState, validating input, revalidating pages, and writing route handlers for APIs.',
          },
        ],
      },
      {
        title: 'Weeks 19–20: Backend with Node.js and Express',
        lessons: [
          {
            title: 'Node.js on the server',
            summary:
              'How Node.js runs JavaScript on a server, modules, the file system, environment variables, npm scripts and a first HTTP server.',
          },
          {
            title: 'Express: routes, params and middleware',
            summary:
              'An Express 5 app, routes and routers, URL params and query strings, JSON bodies, middleware order and a request logger.',
          },
          {
            title: 'REST API design, validation and errors',
            summary:
              'Resources, methods and status codes, validating requests with Zod, consistent error responses and an error-handling middleware.',
          },
          {
            title: 'Testing with Vitest, Supertest and React Testing Library',
            summary:
              'Unit tests with Vitest, API tests with Supertest, component tests with React Testing Library, and testing the unhappy paths.',
          },
          {
            title: 'File uploads, configuration and API safety',
            summary:
              'File uploads with size and type checks, CORS settings, configuration per environment, logging, and keeping secrets out of code.',
          },
          {
            title: 'Project: a REST API for the restaurant app',
            summary:
              'Build and test a menu and orders API with validation and clear errors, document its endpoints and connect the React app to it.',
          },
        ],
      },
      {
        title: 'Weeks 21–22: Databases',
        lessons: [
          {
            title: 'SQL and PostgreSQL: tables, keys and CRUD',
            summary:
              'Relational databases, tables with types and constraints, primary keys, insert, select, update and delete, and a free hosted PostgreSQL database.',
          },
          {
            title: 'SQL: relationships, joins and transactions',
            summary:
              'One-to-many and many-to-many relationships, foreign keys, inner and left joins, group by, indexes, transactions and parameterised queries.',
          },
          {
            title: 'Prisma ORM: schema, migrations and queries',
            summary:
              'Setting up Prisma, models and relations, migrations, typed queries, nested writes and avoiding the N+1 problem.',
          },
          {
            title: 'MongoDB and Mongoose',
            summary:
              'Documents and collections, embedding or referencing, Mongoose schemas and validation, queries, and choosing SQL or NoSQL.',
          },
          {
            title: 'Connecting the API to the database',
            summary:
              'Replacing in-memory data with Prisma, handling not-found and conflict errors, transactions for orders and seeding sample data.',
          },
          {
            title: 'Project: a full-stack app with React, Express and PostgreSQL',
            summary:
              'Put the frontend, the API and the database together, deploy the API and database, and test the whole flow end to end.',
          },
        ],
      },
      {
        title: 'Week 23: Authentication, security and payments',
        lessons: [
          {
            title: 'Authentication: passwords, sessions and JWT',
            summary:
              'Hashing passwords with argon2, sign-up and sign-in, sessions or JWTs, secure cookies and protecting routes.',
          },
          {
            title: 'Authorization and web security',
            summary:
              'Roles and ownership checks, the OWASP Top 10, XSS, CSRF and SQL injection, what CORS does not protect, rate limiting and security headers.',
          },
          {
            title: 'Payments with SSLCommerz, bKash and Stripe',
            summary:
              'How payment gateways work, sandbox payments, verifying payments on the server, webhooks and IPN, and updating orders exactly once.',
          },
        ],
      },
      {
        title: 'Week 24: Full-stack Next.js, AI and launch',
        lessons: [
          {
            title: 'Full-stack Next.js with Prisma and AI features',
            summary:
              'One Prisma client in Next.js, Server Actions with authentication checks, the OpenAI API from the server with streaming, and limits on cost and abuse.',
          },
          {
            title: 'Deployment: Vercel, CI with GitHub Actions and Docker',
            summary:
              'Deploying to Vercel with environment variables and migrations, GitHub Actions for lint, tests and build, and a Docker image for a Node.js API.',
          },
          {
            title: 'Demo day: present your capstone and plan your next steps',
            summary:
              'Final checks, a five-minute project demo, your portfolio, CV and GitHub profile, and how to keep learning after the course.',
          },
        ],
      },
    ],
  },

  {
    slug: 'agentic-ai-engineer-python',
    title: 'AI Agent Engineering with Python',
    subtitle: 'Production-grade AI agents with Python, LangChain & LangGraph',
    description:
      'An advanced Python course for students who already code. Go deep into agentic AI with LangChain, LangGraph, LangSmith, FastAPI, and production-style deployment. Limited to 12 students per batch. Capstone: ship a multi-agent demo. This is a skills course, not an income, job, or paying-user guarantee.',
    instructor: 'Minhaj Sadik',
    level: 'Advanced',
    category: 'AI & Automation',
    tags: ['Python', 'LangChain', 'LangGraph', 'FastAPI', 'Docker', 'AWS'],
    language: 'English + Bangla',
    duration: '12 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: 'Max 12 students',
    price: '৳19,999',
    earlyBirdPrice: '৳14,999',
    promoPriceLabel: 'Founder Batch',
    status: 'coming-soon',
    priority: 3,
    targetStudents:
      'CS graduates, working junior developers, and ML enthusiasts who want international-level AI engineering roles.',
    prerequisites: [
      'Comfortable with one programming language',
      'AI Agent Builder course or equivalent recommended',
      'Basic command-line familiarity',
    ],
    tools: [
      'Python 3.12+',
      'FastAPI',
      'LangChain / LangGraph',
      'LangSmith',
      'OpenAI / Anthropic SDKs',
      'ChromaDB / Pinecone',
      'Hugging Face',
      'MCP Framework',
      'Docker',
      'AWS / Hetzner',
    ],
    highlights: [
      '12-week intensive, limited to 12 seats',
      'Code-first approach with Python',
      'Multi-agent orchestration with LangGraph',
      'Production deployment on AWS/Hetzner',
      'Security: prompt injection & jailbreak prevention',
      'Capstone: ship a multi-agent demo with production-style deployment',
      'Taught personally by Minhaj Sadik',
      'Certificate of completion',
    ],
    pros: [
      'প্রকৃত Python ইঞ্জিনিয়ারিং দক্ষতা তৈরি হয় — নো-কোড টুলের উপর নির্ভরশীল নয়, সম্পূর্ণ কোড-ভিত্তিক',
      'অ্যাডভান্সড বিষয়: মাল্টি-এজেন্ট সিস্টেম, LangGraph, প্রোডাকশন ডিপ্লয়মেন্ট এবং সিকিউরিটি',
      'খুব ছোট ব্যাচ (সর্বোচ্চ ১২ জন) — নিবিড় মেন্টরশিপ এবং সরাসরি ইন্সট্রাক্টরের সাথে যোগাযোগ',
      'পোর্টফোলিও-ফোকাসড ক্যাপস্টোন — কাজ করে এমন ডেমো উপস্থাপনের অনুশীলন',
    ],
    cons: [
      'আগে থেকে প্রোগ্রামিং অভিজ্ঞতা থাকতে হবে — সম্পূর্ণ নতুনদের বা নন-কোডারদের জন্য নয়',
      'Skillo-র সবচেয়ে কঠিন কোর্স — প্রচুর সময় ও শক্তির প্রতিশ্রুতি দরকার',
      'এই রোলের জন্য বাংলাদেশের স্থানীয় চাকরির বাজার এখনো গড়ে উঠছে — প্রধান সুযোগ রিমোট বা আন্তর্জাতিক',
      'প্রি-রিকুইজিট: ভর্তির আগে অন্তত একটি প্রোগ্রামিং ভাষায় স্বাচ্ছন্দ্য থাকতে হবে',
    ],
    careerPaths: [
      'Portfolio-ready Python agent demos for interviews and personal products',
      'Competitive AI / backend engineering roles (not a placement promise)',
      'Optional consulting path if you publish a public demo and pursue clients',
      'Startup experiments with multi-agent systems',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a portfolio-ready multi-agent Python demo with a documented deployment path. Outcomes after that depend on your practice, experience, and goals.',
      jobs: [
        {
          role: 'AI / ML Engineer',
          where: 'Product teams hiring remotely or locally',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path, not a placement promise. A live LangGraph + FastAPI demo matters more than a certificate alone.',
        },
        {
          role: 'Backend AI Engineer',
          where: 'AI startups and product teams',
          salary: 'Role & experience dependent',
          tip: 'Hiring outcomes vary widely by English, communication, prior code experience, and location.',
        },
        {
          role: 'AI Systems Support',
          where: 'Teams running production LLM features',
          salary: 'Varies by company',
          tip: 'Document how you evaluated, traced, and deployed a workflow. Do not present salary bands as a course result.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'LangChain / LangGraph prototype or architecture review',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Custom agent prototype for a specific operations problem',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Show a tested demo and set honest expectations. Do not promise automated revenue.',
        },
      ],
      projects: [
        {
          name: 'AI Research Agent Demo',
          client: 'Portfolio use case: research and reporting workflows',
          value: 'Capstone / portfolio project',
          description:
            'Practice a LangGraph system that researches a topic, compiles a structured report, and publishes a summary with tracing via LangSmith.',
        },
        {
          name: 'Customer Support Agent Backend',
          client: 'Portfolio use case: e-commerce operations',
          value: 'Capstone / portfolio project',
          description:
            'Practice a Python support workflow with escalation logic, CRM-style integration, and observability — presented as a demo, not a ticket-volume claim.',
        },
        {
          name: 'Document Analysis Pipeline',
          client: 'Portfolio use case: contract or report review',
          value: 'Capstone / portfolio project',
          description:
            'Practice ingesting documents, RAG retrieval, and cited summaries so you can explain the architecture in interviews or class reviews.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: Python & LLM Deep Dive',
        lessons: [
          { title: 'Python refresher: OOP, async, type hints' },
          { title: 'LLMs deep-dive: tokens, embeddings, fine-tuning concepts' },
          { title: 'OpenAI & Anthropic SDK mastery' },
        ],
      },
      {
        title: 'Weeks 3–4: LangChain & Agentic RAG',
        lessons: [
          { title: 'LangChain primitives & chains' },
          { title: 'Advanced RAG pipelines' },
          { title: 'Tool-using agents with MCP' },
        ],
      },
      {
        title: 'Weeks 5–6: Planning & Multi-Agent Orchestration',
        lessons: [
          { title: 'ReAct, self-reflection & planning patterns' },
          { title: 'Multi-agent orchestration with LangGraph' },
          { title: 'Agent communication & state management' },
        ],
      },
      {
        title: 'Weeks 7–8: Evaluation & Security',
        lessons: [
          { title: 'LangSmith evaluation & tracing' },
          { title: 'Prompt injection & jailbreak prevention' },
          { title: 'Guardrails & safety frameworks' },
        ],
      },
      {
        title: 'Weeks 9–10: Production Deployment',
        lessons: [
          { title: 'FastAPI service architecture' },
          { title: 'Docker containerization & CI/CD' },
          { title: 'AWS/Hetzner deployment & monitoring' },
        ],
      },
      {
        title: 'Weeks 11–12: Capstone',
        lessons: [
          { title: 'Build & ship a multi-agent SaaS-style demo' },
          { title: 'Document architecture, evaluation, and a realistic next-step plan' },
          { title: 'Final presentation & certification' },
        ],
      },
    ],
  },

  {
    slug: 'react-native-ai',
    title: 'Mobile App Development with React Native + AI',
    subtitle: 'Build cross-platform iOS & Android apps with AI features',
    description:
      'Level up from web to mobile. Build production-ready iOS and Android apps using React Native, Expo, TypeScript, and AI APIs, then practice store submission as a portfolio outcome. Students can apply these skills to studies, employment, startups, or optional freelancing depending on practice and goals. This is a skills course, not an income or job guarantee.',
    instructor: 'Minhaj Sadik',
    level: 'Intermediate',
    category: 'Mobile',
    tags: ['React Native', 'Expo', 'TypeScript', 'Firebase', 'OpenAI API'],
    language: 'Bangla + English',
    duration: '12 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: 'Max 20 students',
    price: '৳12,999',
    earlyBirdPrice: '৳9,999',
    status: 'open',
    priority: 4,
    targetStudents:
      'Web developers leveling up, CS students, and Full Stack Web Dev course graduates.',
    prerequisites: ['JavaScript/TypeScript basics', 'React fundamentals (Course 2 or equivalent)'],
    tools: [
      'React Native',
      'Expo',
      'TypeScript',
      'Firebase / Supabase',
      'RevenueCat',
      'OpenAI API',
      'EAS Build',
    ],
    highlights: [
      '12-week mobile development program',
      'Cross-platform: iOS + Android from one codebase',
      'AI feature integration (chat, voice, image)',
      'Monetization with RevenueCat subscriptions',
      'Publish to App Store & Play Store',
      'Certificate of completion',
    ],
    pros: [
      'iOS এবং Android দুটোর জন্য একটাই কোডবেস — শেখা ও ডেভেলপমেন্টে সময়-সাশ্রয়ী বিনিয়োগ',
      'স্থানীয় স্টার্টআপে মোবাইল ডেভেলপারের চাহিদা আছে — ফলাফল অভিজ্ঞতা, পোর্টফোলিও ও যোগাযোগের উপর নির্ভর করে',
      'নিজের অ্যাপ পাবলিশ করার অনুশীলন — স্টোর সাবমিশন একটি শেখার ফলাফল, আয়ের গ্যারান্টি নয়',
      'Full Stack Web Dev থেকে স্বাভাবিক ধারাবাহিকতা — বিদ্যমান React জ্ঞানের উপর ভিত্তি করে এগোনো যায়',
    ],
    cons: [
      'আগে থেকে React/JavaScript জানতে হবে — সম্পূর্ণ নতুনদের জন্য উপযুক্ত নয়',
      'App Store ও Play Store-এর রিভিউ প্রক্রিয়া ধীর ও অনিশ্চিত হতে পারে',
      'মোবাইল ডেভেলপমেন্ট এনভায়রনমেন্ট সেটআপ (Xcode/Android Studio) কনফিগার করা জটিল হতে পারে',
      'ওয়েব ডেভেলপমেন্ট গিগের তুলনায় প্রথম ফ্রিল্যান্স ক্লায়েন্ট পেতে বেশি সময় লাগে',
    ],
    careerPaths: [
      'Portfolio-ready iOS and Android demos for interviews and personal work',
      'Mobile developer roles at local startups and agencies (competitive, not a placement promise)',
      'Optional freelancing path if you publish a public demo and pursue clients',
      'Personal utility apps and store-submission practice',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a portfolio-ready React Native app and practice App Store / Play Store submission so you have a working demo to present. Outcomes after that depend on your practice and goals.',
      jobs: [
        {
          role: 'React Native Mobile Developer',
          where: 'Local startups and IT firms in Dhaka and Sylhet',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path, not a placement promise. A published or TestFlight/internal demo carries more weight than a certificate alone.',
        },
        {
          role: 'Mobile Developer (Remote)',
          where: 'Product teams hiring cross-platform engineers',
          salary: 'Role & experience dependent',
          tip: 'Hiring outcomes vary widely by English, communication, location, and experience. Keep one public walkthrough of a real app flow.',
        },
        {
          role: 'Cross-Platform App Developer',
          where: 'Agencies building client products',
          salary: 'Varies by company',
          tip: 'Agencies often value Expo familiarity and reliable delivery. Use your capstone to show a specific user flow — not as an earnings claim.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'React Native cross-platform app development',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise. Profiles, proposals, reviews, English, and market demand all affect results.',
        },
        {
          platform: 'Fiverr (optional path)',
          gig: 'Convert an existing web app into a React Native mobile app',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'A clear store listing or demo video helps people understand your skill. Paid work still requires outreach, delivery quality, and patience.',
        },
        {
          platform: 'Self-published apps (optional path)',
          gig: 'Utility apps with ads or subscriptions',
          earning: 'Not guaranteed · product-dependent',
          tip: 'Publishing is a learning outcome. Revenue depends on distribution, product quality, and luck — not on finishing the course.',
        },
      ],
      projects: [
        {
          name: 'Restaurant Mobile Ordering App',
          client: 'Portfolio use case: local restaurants and cafes',
          value: 'Capstone / portfolio project',
          description:
            'Practice a React Native app with menu browsing, cart, QR table scanning, and payment-integration patterns, plus store-submission practice.',
        },
        {
          name: 'Subscription Utility App',
          client: 'Portfolio use case: a focused public utility',
          value: 'Capstone / portfolio project',
          description:
            'Practice a single-purpose app such as a planner, tracker, or local guide, including RevenueCat-style subscription wiring as a technical exercise.',
        },
        {
          name: 'Local Service Marketplace App',
          client: 'Portfolio use case: local service businesses',
          value: 'Capstone / portfolio project',
          description:
            'Practice a two-sided booking flow with ratings, in-app payment patterns, and push notifications — presented as a demo, not a revenue forecast.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: React Native & Expo Setup',
        lessons: [
          { title: 'React Native setup with Expo & TypeScript' },
          { title: 'Core components: View, Text, Image, ScrollView, FlatList' },
          { title: 'Styling with NativeWind (Tailwind for mobile)' },
          { title: 'Navigation: Stack, Tabs & Drawer with Expo Router' },
        ],
      },
      {
        title: 'Weeks 3–4: State, Forms & Data',
        lessons: [
          { title: 'State management with Zustand & React Query' },
          { title: 'Form handling, validation & keyboard management' },
          { title: 'REST API integration & loading/error states' },
          { title: 'Local storage with AsyncStorage & MMKV' },
        ],
      },
      {
        title: 'Weeks 5–6: Backend & Authentication',
        lessons: [
          { title: 'Firebase / Supabase setup & real-time data sync' },
          { title: 'Authentication flows: email, Google, Apple login' },
          { title: 'Push notifications with Expo Notifications' },
          { title: 'Deep linking & URL scheme handling' },
        ],
      },
      {
        title: 'Weeks 7–8: AI Features & Native APIs',
        lessons: [
          { title: 'OpenAI API: chat, image & voice in mobile apps' },
          { title: 'Camera, image picker & media handling' },
          { title: 'Location services & maps integration' },
          { title: 'Animations with Reanimated & Gesture Handler' },
        ],
      },
      {
        title: 'Weeks 9–10: Monetization & Performance',
        lessons: [
          { title: 'RevenueCat subscriptions & in-app purchases' },
          { title: 'Analytics with PostHog & crash reporting (Sentry)' },
          { title: 'Performance optimization: lists, images, bundle size' },
          { title: 'Offline-first patterns & background tasks' },
        ],
      },
      {
        title: 'Weeks 11–12: Publishing & Capstone',
        lessons: [
          { title: 'EAS Build: iOS & Android production builds' },
          { title: 'App Store & Play Store submission process & review tips' },
          { title: 'Capstone: ship a complete AI-powered app to both stores' },
          { title: 'Launch marketing & first 100 users strategy' },
        ],
      },
    ],
  },

  {
    slug: 'ai-video-editor',
    title: 'AI Video Editor for TikTok/YouTube/Reels',
    subtitle: 'Edit viral short-form & long-form video with AI tools',
    description:
      'Learn professional video editing with CapCut, DaVinci Resolve, and AI tools such as Runway ML and ElevenLabs. Build a reel and long-form editing portfolio you can present for class, personal channels, or optional freelance work later. This is a skills course, not an income or job guarantee.',
    instructor: 'Guest YouTuber/Editor',
    level: 'Beginner',
    category: 'Design',
    tags: ['CapCut', 'DaVinci Resolve', 'Runway ML', 'ElevenLabs', 'YouTube'],
    language: 'Bangla',
    duration: '8 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: 'Max 20 students',
    price: '৳7,999',
    earlyBirdPrice: '৳5,999',
    status: 'coming-soon',
    priority: 5,
    targetStudents:
      'Content creators, students, and video hobbyists who want to monetize editing skills.',
    prerequisites: ['Basic computer literacy', 'Own laptop with 16GB+ RAM (for editing software)'],
    tools: [
      'DaVinci Resolve (free)',
      'CapCut Pro',
      'Adobe Premiere Pro (optional — free alternatives covered)',
      'Descript',
      'Runway ML',
      'ElevenLabs',
      'Submagic',
      'Opus Clip',
      'ChatGPT (scripts)',
    ],
    highlights: [
      '8-week hands-on program',
      'Short-form & long-form editing mastery',
      'AI video tools (Runway ML, ElevenLabs)',
      'Edit 10 reels + 1 long-form video',
      'Certificate of completion',
    ],
    pros: [
      'কোডিং দক্ষতা লাগবে না — সম্পূর্ণ ক্রিয়েটিভ দক্ষতানির্ভর, সবার জন্য সুলভ',
      'শর্ট-ফর্ম ও লং-ফর্ম দুই ধরনের এডিটিং অনুশীলন — রিল ও YouTube-স্টাইল প্রজেক্ট',
      'AI টুলস (Runway, ElevenLabs) দিয়ে দ্রুত ড্রাফট তৈরির ওয়ার্কফ্লো শেখা যায়',
      'পোর্টফোলিও-ফোকাসড — ক্লাস প্রজেক্টই পরবর্তী পথের প্রমাণ',
    ],
    cons: [
      'ভিডিও এডিটিং সফটওয়্যার হার্ডওয়্যার-নির্ভর হওয়ায় ১৬GB+ RAM-এর ল্যাপটপ দরকার',
      'গেস্ট ইন্সট্রাক্টর কোর্স পরিচালনা করবেন — মিনহাজ পরিচালিত কোর্সের মতো সরাসরি সম্পর্ক নয়',
      'ক্লায়েন্টের রিভিশন ও ব্যক্তিগত রুচির কারণে কিছু শিক্ষার্থীর জন্য কাজটি চাপের হতে পারে',
      'YouTube বা প্ল্যাটফর্ম মনিটাইজেশন কোনো গ্যারান্টি নয় — দর্শক, ধারাবাহিকতা ও প্ল্যাটফর্ম নীতির উপর নির্ভর করে',
    ],
    careerPaths: [
      'Portfolio-ready short-form and long-form edits for class, personal channels, or interviews',
      'Optional freelance editing if you publish demos and pursue clients',
      'In-house content support roles (competitive, not a placement promise)',
      'Personal YouTube or Reels practice — not a monetization guarantee',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a portfolio of short-form reels plus one long-form edit you can present. Paid work after that depends on practice, outreach, and market demand.',
      jobs: [
        {
          role: 'Video Editor / Content Creator',
          where: 'Marketing teams and channels that hire editors',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path. A public before/after reel matters more than a certificate.',
        },
        {
          role: 'Social Media Content Support',
          where: 'Local brands and agencies',
          salary: 'Role & experience dependent',
          tip: 'Hiring still depends on the employer. Do not treat class projects as a job offer.',
        },
      ],
      freelance: [
        {
          platform: 'Fiverr (optional path)',
          gig: 'Short-form reel editing',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Upwork (optional path)',
          gig: 'YouTube or social-video editing',
          earning: 'Not guaranteed · effort-based',
          tip: 'Profiles, proposals, English, reviews, and delivery speed all affect results.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Monthly content edits for a shop or creator',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Show a tested demo. Do not promise views, virality, or ad revenue.',
        },
      ],
      projects: [
        {
          name: 'Faceless Channel Practice Cut',
          client: 'Portfolio use case: educational or explainer video',
          value: 'Capstone / portfolio project',
          description:
            'Practice AI voiceover, stock footage, captions, and pacing so you can explain your editing decisions.',
        },
        {
          name: 'Local Business Reel Set',
          client: 'Portfolio use case: restaurants, salons, or shops',
          value: 'Capstone / portfolio project',
          description:
            'Edit a set of short reels with hooks, text, and color — presented as a demo pack, not a retainer forecast.',
        },
        {
          name: 'Long-form Lesson Edit',
          client: 'Portfolio use case: a class or workshop recording',
          value: 'Capstone / portfolio project',
          description:
            'Practice transcription-based cuts, captions, and audio polish on one longer video.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: Storytelling & CapCut Mastery',
        lessons: [
          { title: 'Short-form storytelling: hooks, pacing & retention' },
          { title: 'CapCut Pro: cuts, transitions, keyframes & effects' },
          { title: 'Text animations, captions & trending templates' },
          { title: 'Edit 3 complete reels from raw footage' },
        ],
      },
      {
        title: 'Weeks 3–4: DaVinci Resolve & Color Grading',
        lessons: [
          { title: 'DaVinci Resolve interface, timeline & multicam editing' },
          { title: 'Color grading fundamentals & LUTs' },
          { title: 'Sound design, music selection & audio mixing' },
          { title: 'Export settings for YouTube, Instagram & TikTok' },
        ],
      },
      {
        title: 'Weeks 5–6: AI Video Tools',
        lessons: [
          { title: 'Runway ML: AI video generation & scene extension' },
          { title: 'ElevenLabs voice cloning & AI voiceover' },
          { title: 'Submagic captions & Opus Clip for repurposing' },
          { title: 'Descript: text-based editing & filler word removal' },
        ],
      },
      {
        title: 'Weeks 7–8: Monetization & Capstone',
        lessons: [
          { title: 'Faceless YouTube channel setup & automation' },
          { title: 'Fiverr gig creation & client pricing strategy' },
          { title: 'Capstone: deliver 10 reels + 1 long-form video for a real client' },
          { title: 'Portfolio reel & first paid order acquisition' },
        ],
      },
    ],
  },

  {
    slug: 'figma-uiux-ai',
    title: 'Figma UI/UX + AI Design Sprint',
    subtitle: 'Design beautiful products with Figma & AI design tools',
    description:
      'Learn UI/UX design from fundamentals to advanced prototyping using Figma, Framer, and AI-assisted design tools. Build a professional portfolio of case studies you can present for class, internships, or optional freelance work. This is a skills course, not an income or job guarantee.',
    instructor: 'Minhaj Sadik & Guest Designer',
    level: 'Beginner',
    category: 'Design',
    tags: ['Figma', 'UI/UX', 'Framer', 'v0.dev', 'Lovable', 'Midjourney'],
    language: 'Bangla + English',
    duration: '10 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: 'Max 20 students',
    price: '৳9,999',
    earlyBirdPrice: '৳7,999',
    status: 'coming-soon',
    priority: 6,
    targetStudents:
      'Aspiring designers, frontend developers wanting design skills, and creative students.',
    prerequisites: ['Basic computer literacy', 'Creative interest'],
    tools: ['Figma + Figma AI', 'FigJam', 'Framer', 'Midjourney', 'v0.dev', 'Lovable', 'Cursor'],
    highlights: [
      '10-week design program',
      'Figma Auto Layout & components mastery',
      'AI-assisted design workflows',
      'Professional portfolio on Behance & Dribbble',
      '3 complete case-study products',
      'Certificate of completion',
    ],
    pros: [
      'ডিজাইন দক্ষতা যেকোনো টেক পথে কাজে লাগে — ডেভেলপার, মার্কেটার ও ফাউন্ডার সবাই উপকৃত হন',
      'পোর্টফোলিওনির্ভর শেখা — Behance-স্টাইল কেস স্টাডি ক্লাস ও ইন্টারভিউতে দেখানো যায়',
      'Figma Auto Layout, components ও AI-assisted workflows একসাথে অনুশীলন',
      'ফ্রিল্যান্সিং একটি সম্ভাব্য পথ মাত্র — কোর্সের প্রতিশ্রুতি নয়',
    ],
    cons: [
      'বেশি পারিশ্রমিকের ক্লায়েন্ট পেতে আরও পোর্টফোলিও তৈরি করতে হয় — ধৈর্য রাখতে হবে',
      'জুনিয়র ডিজাইনারদের জন্য প্রতিযোগিতামূলক বাজার — আলাদা পরিচয় তৈরিতে সময় ও নিয়মিত কাজ লাগে',
      'ডিজাইনের রুচি ধীরে ধীরে বিকশিত হয় — কিছু শিক্ষার্থী অন্যদের চেয়ে দ্রুত এগোতে পারে',
      'ডিজাইনারদের স্থানীয় বাজারের রেট ডেভেলপার বা AI অটোমেশন স্পেশালিস্টদের চেয়ে কম',
    ],
    careerPaths: [
      'Portfolio-ready UI case studies for internships, interviews, and personal products',
      'Junior product/UI roles (competitive, not a placement promise)',
      'Optional freelance design if you publish demos and pursue clients',
      'Better collaboration with developers through structured Figma files',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, publish three case-study products in a public portfolio you can present. Paid work after that depends on practice, communication, and demand.',
      jobs: [
        {
          role: 'Junior UI/UX Designer',
          where: 'Product teams and agencies',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path. Clear problem → process → screens case studies matter more than a certificate.',
        },
        {
          role: 'Product Designer (early-career)',
          where: 'Startups hiring their first designer',
          salary: 'Role & experience dependent',
          tip: 'Hiring outcomes vary widely. Do not treat salary bands as a course result.',
        },
        {
          role: 'Design support in an agency',
          where: 'Studios taking client UI work',
          salary: 'Varies by company',
          tip: 'Agencies value reliable Figma files and communication, not income promises.',
        },
      ],
      freelance: [
        {
          platform: 'Fiverr (optional path)',
          gig: 'App or landing-page UI in Figma',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Upwork (optional path)',
          gig: 'Website UI with developer handoff',
          earning: 'Not guaranteed · effort-based',
          tip: 'Organized components help, but paid work still needs outreach and reviews.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Brand + simple website UI for a small business',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Show a tested prototype. Do not promise more customers from a redesign.',
        },
      ],
      projects: [
        {
          name: 'Mobile App UI Redesign',
          client: 'Portfolio use case: an existing MVP flow',
          value: 'Capstone / portfolio project',
          description:
            'Practice improved user flows, a component library, and an interactive prototype with a written UX rationale.',
        },
        {
          name: 'SaaS Dashboard Design System',
          client: 'Portfolio use case: B2B product UI',
          value: 'Capstone / portfolio project',
          description:
            'Practice a Figma component library with typography, color, and developer-friendly structure.',
        },
        {
          name: 'Local Business Brand + Website UI',
          client: 'Portfolio use case: a cafe, clinic, or shop',
          value: 'Capstone / portfolio project',
          description:
            'Practice logo, color, and a small website UI presented as a case study rather than a sales forecast.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: Design Principles & Figma Basics',
        lessons: [
          { title: 'Design principles: color, typography, spacing, hierarchy' },
          { title: 'Figma interface, frames, shapes & basic tools' },
          { title: 'Recreating real UI screens (mobile + web)' },
          { title: 'Responsive design & constraints' },
        ],
      },
      {
        title: 'Weeks 3–4: Components & Design Systems',
        lessons: [
          { title: 'Auto Layout, variants & component architecture' },
          { title: 'Building a design system from scratch' },
          { title: 'Mobile-first UI patterns & card layouts' },
          { title: 'Design tokens & style documentation' },
        ],
      },
      {
        title: 'Weeks 5–6: User Research & Prototyping',
        lessons: [
          { title: 'User research lite: personas, empathy maps & user flows' },
          { title: 'Wireframing: low-fi to high-fi process' },
          { title: 'Interactive prototyping & micro-animations' },
          { title: 'Usability testing & iteration' },
        ],
      },
      {
        title: 'Weeks 7–8: AI-Assisted Design',
        lessons: [
          { title: 'Figma AI features & smart suggestions' },
          { title: 'v0.dev & Lovable for design-to-code' },
          { title: 'AI-generated assets with Midjourney' },
          { title: 'Framer: publish a live portfolio site from Figma' },
        ],
      },
      {
        title: 'Weeks 9–10: Portfolio & Capstone',
        lessons: [
          { title: 'Portfolio building on Behance & Dribbble' },
          { title: 'Writing compelling case studies' },
          { title: 'Capstone: 3 complete case-study products with UX write-ups' },
          { title: 'Optional freelance profile basics (no income promise)' },
        ],
      },
    ],
  },

  {
    slug: 'graphic-design-ai',
    title: 'Graphic Design with AI',
    subtitle: 'Canva + Photoshop + Midjourney for modern designers',
    description:
      'Learn Canva, Photoshop, Illustrator, and AI image generation with Midjourney and Leonardo. Build a beginner graphic-design portfolio you can present for class, personal projects, or optional freelance work. This is a skills course, not an income or job guarantee.',
    instructor: 'Guest Instructor',
    level: 'Beginner',
    category: 'Design',
    tags: ['Canva', 'Photoshop', 'Illustrator', 'Midjourney', 'Leonardo AI'],
    language: 'Bangla',
    duration: '8 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: 'Max 20 students',
    price: '৳6,999',
    earlyBirdPrice: '৳4,999',
    status: 'coming-soon',
    priority: 7,
    targetStudents:
      'Beginners, students, and anyone with creative interest — no prior experience needed.',
    prerequisites: ['Basic computer literacy'],
    tools: [
      'Canva',
      'Adobe Photoshop',
      'Adobe Illustrator',
      'Midjourney',
      'Leonardo AI',
      'Adobe Express',
      'Remove.bg',
    ],
    highlights: [
      '8-week creative program',
      'Canva + Photoshop + Illustrator mastery',
      'AI image generation (Midjourney & Leonardo)',
      'Brand identity design fundamentals',
      '5 portfolio brands shipped',
      'Certificate of completion',
    ],
    pros: [
      'একদম নতুনদের জন্য — কোনো পূর্ব দক্ষতা বা অভিজ্ঞতা একেবারেই লাগবে না',
      'Canva, Photoshop ও AI ইমেজ টুলস একসাথে অনুশীলন',
      'লোকাল ব্যবসা ও ব্যক্তিগত প্রজেক্টে ব্যবহারযোগ্য গ্রাফিক দক্ষতা',
      'ফ্রিল্যান্সিং একটি সম্ভাব্য পথ মাত্র — কোর্সের প্রতিশ্রুতি নয়',
    ],
    cons: [
      'গ্লোবাল প্ল্যাটফর্মে প্রতিযোগিতা বেশি — পোর্টফোলিও ও ধৈর্য লাগে',
      'গেস্ট ইন্সট্রাক্টর কোর্স পরিচালনা করবেন — মিনহাজ সাদিক সরাসরি মেন্টর করবেন না',
      'টুল লাইসেন্স (Canva Pro, Adobe, Midjourney) ভর্তির সময় নিশ্চিত হয় — ফ্রিতে পাওয়ার গ্যারান্টি নেই',
      'মার্কেটিং বা কন্টেন্ট দক্ষতার সাথে না মেশালে শুধু ডিজাইন অনুশীলন সীমিত থাকতে পারে',
    ],
    careerPaths: [
      'Portfolio-ready logos, thumbnails, and social graphics',
      'Junior graphic or print-shop support roles (competitive, not a placement promise)',
      'Optional freelance design if you publish demos and pursue clients',
      'Personal and local-business creative practice',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a small brand kit and social-graphic set you can present. Paid work after that depends on practice, outreach, and demand.',
      jobs: [
        {
          role: 'Junior Graphic Designer',
          where: 'Print shops, signage companies, and local agencies',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path. A printed sample pack of logo, card, and poster work is stronger than a certificate.',
        },
        {
          role: 'Social Media Designer',
          where: 'Teams that need regular creative assets',
          salary: 'Role & experience dependent',
          tip: 'Hiring still depends on the employer. Do not treat class templates as a retainer.',
        },
      ],
      freelance: [
        {
          platform: 'Fiverr (optional path)',
          gig: 'Logo, thumbnail, or social post design',
          earning: 'Not guaranteed · portfolio-first',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Simple branding and print files for a shop',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Show samples. Do not promise more customers from a logo.',
        },
      ],
      projects: [
        {
          name: 'Complete Brand Identity Practice',
          client: 'Portfolio use case: a new local shop',
          value: 'Capstone / portfolio project',
          description:
            'Practice logo, color, type, a business card, and a social starter kit in Canva and Illustrator.',
        },
        {
          name: 'YouTube Thumbnail Set',
          client: 'Portfolio use case: a sample channel',
          value: 'Capstone / portfolio project',
          description:
            'Practice click-oriented thumbnail layouts in Photoshop with AI-assisted backgrounds as a technical exercise.',
        },
        {
          name: 'Social Media Content Pack',
          client: 'Portfolio use case: a local brand',
          value: 'Capstone / portfolio project',
          description:
            'Practice a month of promotional posts, carousels, and story templates in consistent brand colors.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: Design Theory & Canva Mastery',
        lessons: [
          { title: 'Design theory: color, typography, composition & hierarchy' },
          { title: 'Canva mastery: social media posts, stories & carousels' },
          { title: 'Canva for print: posters, business cards & flyers' },
          { title: 'Creating branded templates & content calendars' },
        ],
      },
      {
        title: 'Weeks 3–4: Photoshop & Illustrator',
        lessons: [
          { title: 'Photoshop: layers, masks, selections & retouching' },
          { title: 'Photoshop: compositing, manipulation & photo editing' },
          { title: 'Illustrator: vector shapes, pen tool & logo design' },
          { title: 'Illustrator: icons, typography & scalable assets' },
        ],
      },
      {
        title: 'Weeks 5–6: AI Image Generation',
        lessons: [
          { title: 'Midjourney prompting: styles, parameters & consistent branding' },
          { title: 'Leonardo AI for product mockups & backgrounds' },
          { title: 'Brand identity sets: logo + colors + business card + social kit' },
          { title: 'YouTube thumbnails: psychology, templates & A/B testing' },
        ],
      },
      {
        title: 'Weeks 7–8: Portfolio & Capstone',
        lessons: [
          { title: 'Building a Behance-style portfolio with 5 projects' },
          { title: 'Social media kits and honest scoping for optional client work' },
          { title: 'Optional freelance profile basics (no income promise)' },
          { title: 'Capstone: 5 portfolio brands with presentation notes' },
        ],
      },
    ],
  },

  {
    slug: 'seo-content-ai',
    title: 'SEO & Content Strategy in the AI Era',
    subtitle: 'Rank in Google & AI search with AI-powered content workflows',
    description:
      'SEO has changed with AI search (SGE, AI Overviews). Learn modern SEO, GEO (Generative Engine Optimization), and AI content workflows you can apply to class sites, personal blogs, or optional client work. This is a skills course, not an income or traffic guarantee.',
    instructor: 'Minhaj Sadik & Guest SEO Expert',
    level: 'Intermediate',
    category: 'Marketing',
    tags: ['SEO', 'Content Strategy', 'Google', 'Ubersuggest', 'ChatGPT', 'GEO'],
    language: 'Bangla + English',
    duration: '8 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: '15–20 students',
    price: '৳8,999',
    earlyBirdPrice: '৳6,999',
    status: 'coming-soon',
    priority: 8,
    targetStudents: 'Writers, marketers, blog owners, and content creators wanting SEO skills.',
    prerequisites: ['Basic computer literacy', 'Comfort with writing in English or Bangla'],
    tools: [
      'Google Search Console',
      'Ubersuggest (free tier)',
      'ChatGPT / Claude',
      'Perplexity',
      'Frase',
      'WordPress',
    ],
    highlights: [
      '8-week content & SEO program',
      'Modern SEO for AI search (SGE/AI Overviews)',
      'GEO — Generative Engine Optimization',
      'AI content workflow: research → publish',
      'Capstone: publish and document a real page — traffic is not guaranteed',
      'Certificate of completion',
    ],
    pros: [
      'AI সার্চের সাথে SEO বদলেছে — GEO ও আধুনিক কনটেন্ট ওয়ার্কফ্লো অনুশীলন করা যায়',
      'স্থানীয় ব্যবসার ওয়েবসাইটে প্রয়োগযোগ্য — গ্যারান্টিড ট্রাফিক নয়',
      'রিসার্চ থেকে পাবলিশ পর্যন্ত একটি পরিষ্কার প্রসেস শেখা যায়',
      'ফ্রিল্যান্সিং একটি সম্ভাব্য পথ মাত্র — কোর্সের প্রতিশ্রুতি নয়',
    ],
    cons: [
      'ফলাফল পেতে সময় লাগে — SEO হলো মাসের পর মাসের বিষয়, দ্রুত আয়ের দক্ষতা নয়',
      'ইন্টারমিডিয়েট লেভেল — ভর্তির আগে ইংরেজি বা বাংলায় লেখালেখিতে স্বাচ্ছন্দ্য থাকতে হবে',
      'ঘন ঘন অ্যালগরিদম পরিবর্তন সারাজীবন নতুন করে শেখা ও মানিয়ে নেওয়ার দাবি রাখে',
      'পেইড টুল (Ahrefs, Surfer) ভর্তির সময় নিশ্চিত হয় — গ্রুপ অ্যাক্সেসের গ্যারান্টি নেই',
    ],
    careerPaths: [
      'Portfolio-ready SEO audits and content samples',
      'In-house content or SEO support roles (competitive, not a placement promise)',
      'Optional freelance SEO if you publish case notes and pursue clients',
      'Personal site practice — traffic and affiliate income are not guaranteed',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete an SEO audit and publish a documented page using Search Console. Rankings and traffic after that depend on the site, competition, and time.',
      jobs: [
        {
          role: 'SEO Specialist / Content Marketer',
          where: 'Agencies and product teams',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path. One documented before/after process is stronger than promised traffic numbers.',
        },
        {
          role: 'Content Strategy Support',
          where: 'Brands publishing regular articles',
          salary: 'Role & experience dependent',
          tip: 'GEO is a useful skill label, not a salary guarantee.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'SEO audit and on-page recommendations',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Local SEO setup notes for a small website',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Do not promise first-page rankings or a retainer from a free audit.',
        },
      ],
      projects: [
        {
          name: 'Practice Content Site',
          client: 'Portfolio use case: a niche explainer site',
          value: 'Capstone / portfolio project',
          description:
            'Practice AI-assisted research, GEO-aware drafting, and Search Console setup. Affiliate income is not a course outcome.',
        },
        {
          name: 'Local Business SEO Sample',
          client: 'Portfolio use case: a restaurant, clinic, or shop site',
          value: 'Capstone / portfolio project',
          description:
            'Practice Google Business Profile notes, on-page fixes, and a Search Console report template.',
        },
        {
          name: 'Brand Content Hub Sample',
          client: 'Portfolio use case: a service brand',
          value: 'Capstone / portfolio project',
          description:
            'Practice an editorial workflow with E-E-A-T signals and citations — presented as a demo, not a traffic forecast.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: How Search Works Now',
        lessons: [
          { title: 'SGE, AI Overviews & the new search landscape' },
          { title: 'Keyword research with Ubersuggest & Google Search Console' },
          { title: 'Search intent analysis & SERP feature mapping' },
          { title: 'Competitor analysis & content gap identification' },
        ],
      },
      {
        title: 'Weeks 3–4: On-Page & Technical SEO',
        lessons: [
          { title: 'On-page SEO: titles, meta, headings, internal linking' },
          { title: 'Content optimization with Surfer SEO' },
          { title: 'Technical SEO: Core Web Vitals, mobile-first, crawlability' },
          { title: 'Schema markup & structured data implementation' },
        ],
      },
      {
        title: 'Weeks 5–6: AI Content & Link Building',
        lessons: [
          { title: 'AI content workflow: research → outline → draft → human polish' },
          { title: 'Content quality checklist: E-E-A-T & helpful content signals' },
          { title: 'Link building strategies that still work' },
          { title: 'GEO optimization: getting cited in AI answers' },
        ],
      },
      {
        title: 'Weeks 7–8: Reporting & Capstone',
        lessons: [
          { title: 'Google Analytics 4 & Search Console reporting' },
          { title: 'Client communication: monthly reports & action plans' },
          { title: 'Pricing SEO services & retainer proposals' },
          { title: 'Capstone: publish a real client page, get indexed, pitch a monthly retainer' },
        ],
      },
    ],
  },

  {
    slug: 'ai-digital-marketing',
    title: 'AI-Powered Digital Marketing & Meta Ads',
    subtitle: 'Master Meta Ads, Google Ads & AI-driven marketing',
    description:
      'Learn digital marketing with AI tools for copywriting, creative production, and campaign optimization. Practice Meta Ads, Google Ads, and reporting so you can present a campaign case study. This is a skills course, not an income, ROAS, or paid-budget guarantee.',
    instructor: 'Minhaj Sadik & Guest Expert',
    level: 'Beginner',
    category: 'Marketing',
    tags: ['Meta Ads', 'Google Ads', 'SEO', 'ChatGPT', 'Canva', 'Analytics'],
    language: 'Bangla',
    duration: '8 weeks',
    schedule: '2 classes/week × 2 hours',
    batchSize: '15–20 students',
    price: '৳9,999',
    earlyBirdPrice: '৳7,499',
    status: 'coming-soon',
    priority: 9,
    targetStudents:
      'Business-minded students, small-business owners, women re-entering work, and anyone wanting digital marketing skills.',
    prerequisites: ['Basic computer literacy', 'Active Facebook account'],
    tools: [
      'Meta Ads Manager',
      'Google Ads',
      'GA4 / Search Console',
      'Canva',
      'ChatGPT / Midjourney',
      'CapCut',
      'Mailchimp',
      'n8n (light automation)',
    ],
    highlights: [
      '8-week practical program',
      'Practice campaign setup, targeting, and reporting (live ad spend confirmed at intake)',
      'AI-powered creative production',
      'Google Analytics 4 & reporting',
      'Capstone: real campaign for local Sylhet business',
      'Certificate of completion',
    ],
    pros: [
      'মেটা ও গুগল অ্যাডসের সেটআপ, টার্গেটিং ও রিপোর্টিং অনুশীলন — শুধু তত্ত্ব নয়',
      'সিলেটের ব্যবসার মতো কেস স্টাডিতে প্রয়োগযোগ্য, গ্যারান্টিড ক্লায়েন্ট নয়',
      'AI দিয়ে ক্রিয়েটিভ ড্রাফট তৈরির ওয়ার্কফ্লো শেখা যায়',
      'ফ্রিল্যান্সিং একটি সম্ভাব্য পথ মাত্র — কোর্সের প্রতিশ্রুতি নয়',
    ],
    cons: [
      'বিজ্ঞাপনের পারফরম্যান্স অনিশ্চিত — ফলাফল বাজেট, টার্গেটিং ও প্রতিযোগিতার উপর নির্ভর করে',
      'প্ল্যাটফর্মের নীতি পরিবর্তন (Meta, Google) ক্যারিয়ার জুড়ে নিয়মিত নতুন করে শেখার দাবি রাখে',
      'ফ্রিল্যান্স রেট ও ROAS অনিশ্চিত — কেস স্টাডি তৈরি করতে সময় ও বাজেট লাগে',
      'শুধু বাংলায় পড়ানো হয় — ইংরেজি আন্তর্জাতিক শেখার রিসোর্স ও ক্লায়েন্ট মার্কেটে প্রবেশাধিকার সীমিত',
    ],
    careerPaths: [
      'Campaign case studies for interviews, class, or a personal brand',
      'In-house marketing support roles (competitive, not a placement promise)',
      'Optional freelance ads work if you publish demos and pursue clients',
      'Practice running structured campaigns — spend and ROAS are not guaranteed',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, complete a campaign case study covering pixel setup, targeting, creatives, and a reporting dashboard. Live ad spend is confirmed at intake and is not promised in the fee.',
      jobs: [
        {
          role: 'Digital Marketing Specialist',
          where: 'Agencies and e-commerce teams',
          salary: 'Market-dependent',
          tip: 'Treat hiring as a competitive path. A documented campaign process is stronger than promised ROAS.',
        },
        {
          role: 'Social Media / Paid Ads Support',
          where: 'Brands running Facebook or Instagram ads',
          salary: 'Role & experience dependent',
          tip: 'Hiring still depends on the employer. Do not treat class projects as a retainer.',
        },
      ],
      freelance: [
        {
          platform: 'Upwork (optional path)',
          gig: 'Meta Ads setup and reporting',
          earning: 'Not guaranteed · effort-based',
          tip: 'Freelancing is one possible application of these skills — not the course promise.',
        },
        {
          platform: 'Direct / local (optional path)',
          gig: 'Campaign setup notes for a shop already boosting posts',
          earning: 'Not guaranteed · relationship-based',
          tip: 'Show a sample report. Do not promise leads, sales, or a monthly retainer.',
        },
      ],
      projects: [
        {
          name: 'Local SME Campaign Case Study',
          client: 'Portfolio use case: a restaurant, clinic, or shop',
          value: 'Capstone / portfolio project',
          description:
            'Practice AI-assisted creatives, targeting, A/B test notes, and a ROAS report template — results depend on budget and market.',
        },
        {
          name: 'E-Commerce Ads Workflow',
          client: 'Portfolio use case: an online shop',
          value: 'Capstone / portfolio project',
          description:
            'Practice Meta and Google campaign structure, Mailchimp-style follow-up, and a GA4 dashboard dummy report.',
        },
        {
          name: 'Launch Campaign Plan',
          client: 'Portfolio use case: a new local business',
          value: 'Capstone / portfolio project',
          description:
            'Practice page setup, pixel installation, a two-week launch plan, and a Month 1 content calendar.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Weeks 1–2: Marketing Fundamentals & Funnels',
        lessons: [
          { title: 'Marketing fundamentals: AIDA, funnels & customer journey' },
          { title: 'Facebook Business Page setup & optimization' },
          { title: 'Meta Pixel installation & event tracking' },
          { title: 'Audience psychology & buyer persona creation' },
        ],
      },
      {
        title: 'Weeks 3–4: Meta Ads Mastery',
        lessons: [
          { title: 'Campaign objectives, ad sets & placement strategies' },
          { title: 'Audience research: custom, lookalike & interest-based targeting' },
          { title: 'AI creative production: Canva + Midjourney + CapCut for ads' },
          { title: 'A/B testing, budget scaling & optimization rules' },
        ],
      },
      {
        title: 'Weeks 5–6: Google Ads & Analytics',
        lessons: [
          { title: 'Google Ads: search campaigns, keywords & bidding' },
          { title: 'Display & YouTube ads basics' },
          { title: 'GA4 setup: events, conversions & custom reports' },
          { title: 'Building client reporting dashboards' },
        ],
      },
      {
        title: 'Weeks 7–8: Email Marketing & Capstone',
        lessons: [
          { title: 'Email marketing with Mailchimp: sequences, automation & segmentation' },
          { title: 'SEO foundations & local SEO for small businesses' },
          { title: 'Client pricing: packages, retainers & proposals' },
          {
            title:
              'Capstone: campaign case study with targeting, creatives, and a reporting dashboard',
          },
        ],
      },
    ],
  },

  {
    slug: 'basic-computer-english',
    title: 'Basic Computer & English for Tech',
    subtitle: 'The foundation course for absolute beginners',
    description:
      "Can't navigate a computer confidently? Struggle with English instructions on apps and websites? This 6-week foundation course teaches you essential computer skills and practical English — so you can confidently join any Skillo tech program afterward.",
    instructor: 'Naeim Ahmed',
    supportInstructor: 'Minhaj Sadik',
    level: 'Beginner',
    category: 'Foundation',
    tags: ['Computer Basics', 'English', 'Typing', 'Google Workspace', 'Internet'],
    language: 'Bangla',
    duration: '6 weeks',
    schedule: '3 classes/week × 1.5 hours',
    batchSize: '20–25 students',
    price: '৳4,999',
    earlyBirdPrice: '৳2,999',
    priceRationale: {
      heading: 'Why this foundation course costs ৳2,999',
      summary:
        'This is priced as a guided 6-week foundation program, not a bundle of recorded tutorials. Students pay for live practice, feedback, and the confidence to use real digital tools independently.',
      points: [
        '27 hours of live class time across 18 hands-on sessions, so beginners can practice with a teacher instead of struggling alone.',
        'Small batch support, checking, and correction for typing, email, Google Docs, Sheets, Zoom, WhatsApp, file handling, and safe internet tasks.',
        'Practical English for apps, websites, messages, and online learning, which makes future AI, web, design, and freelancing courses easier to follow.',
        'A lower early-bird fee than the regular ৳4,999 price, saving students ৳2,000 before the batch fills.',
      ],
    },
    status: 'open',
    priority: 10,
    targetStudents:
      'HSC/SSC students, housewives, shop owners, and anyone in Sylhet who wants to enter the tech world but lacks basic computer and English skills.',
    prerequisites: ['No prior experience needed', 'Own or have access to a laptop/desktop'],
    tools: [
      'Windows / macOS basics',
      'Google Chrome',
      'Gmail',
      'Google Drive / Docs / Sheets / Slides',
      'Bangla & English typing (Avro / Google Input)',
      'WhatsApp / Zoom',
      'ChatGPT (basic usage)',
    ],
    highlights: [
      '6-week foundation program',
      'Zero to confident computer user',
      'Practical English for tech interfaces',
      'Google Workspace proficiency',
      'Internet safety & digital literacy',
      'Direct pathway into Skillo tech courses',
      'Certificate of completion',
    ],
    pros: [
      'কোনো পূর্বশর্ত নেই — প্রযুক্তিগত কোনো পটভূমি না থাকলেও নিখুঁত শুরুর জায়গা',
      'যেকোনো ডিজিটাল টুল, অ্যাপ বা প্ল্যাটফর্ম স্বাধীনভাবে ব্যবহার করার আত্মবিশ্বাস তৈরি হয়',
      'ব্যবহারিক ইংরেজি দক্ষতা সমস্ত বৈশ্বিক শেখার রিসোর্স ও টিউটোরিয়ালে প্রবেশের সুযোগ দেয়',
      'Skillo-তে সবচেয়ে সাশ্রয়ী বিকল্প — প্রযুক্তি যাত্রা শুরুর সর্বনিম্ন বিনিয়োগ',
      'যেকোনো অ্যাডভান্সড Skillo কোর্সে যাওয়ার সরাসরি পথ — প্রয়োজনীয় ভিত্তি তৈরি করে',
    ],
    cons: [
      'এটি একা আয়ের কোর্স নয় — পরবর্তী টেক কোর্সের জন্য কম্পিউটার ও ইংরেজি ভিত্তি তৈরি করে',
      'চাকরি, ফ্রিল্যান্সিং বা মাইক্রো-টাস্ক আয়ের কোনো গ্যারান্টি নেই',
      'অভিজ্ঞ কম্পিউটার ব্যবহারকারীরা এই কোর্সটিকে অনেক বেসিক মনে করবেন — তাদের সরাসরি উচ্চতর কোর্সে শুরু করা উচিত',
    ],
    careerPaths: [
      'Ready to join AI Agent Builder or another Skillo tech course',
      'Stronger computer, email, and Google Workspace confidence for school or office tasks',
      'Optional beginner digital-work practice after counselling — not a promised income path',
      'Safer internet habits and practical English for tutorials and apps',
    ],
    outcomes: {
      firstMilestone:
        'By the end of the course, use a computer independently for files, email, Google Workspace, typing, and safe internet tasks, and prepare a simple CV you can reuse when joining a later Skillo tech course.',
      jobs: [
        {
          role: 'Office Assistant / Data Entry Operator',
          where: 'Small businesses, NGOs, and local offices in Sylhet',
          salary: 'Market-dependent',
          tip: 'Treat this as a possible later path, not a placement promise. A clear Google Docs CV and basic Workspace confidence help, but hiring still depends on the employer.',
        },
        {
          role: 'Customer Support Representative',
          where: 'Local businesses handling messages and online orders',
          salary: 'Role & experience dependent',
          tip: 'Practical English and computer confidence are useful, but these roles are competitive and are not guaranteed by the course.',
        },
      ],
      freelance: [
        {
          platform: 'Optional beginner practice',
          gig: 'Simple digital tasks after counselling, if it fits your goals',
          earning: 'Not guaranteed · foundation course',
          tip: 'This course does not promise Picoworkers, Fiverr, or other paid micro-task income. Any later freelance attempt needs extra practice, a profile, and patience.',
        },
      ],
      projects: [
        {
          name: 'Prepare for Your Next Skillo Course',
          client: 'Your own learning path',
          value: 'Foundation / practice project',
          description:
            'Finish with computer, email, and English confidence so AI Agent Builder, Full Stack, or another Skillo program is easier to follow.',
        },
        {
          name: 'Organized Google Workspace Folder',
          client: 'Practice use case: school, family, or a small shop',
          value: 'Foundation / practice project',
          description:
            'Create and share a Drive folder with a Docs CV, a Sheets list, and a Slides summary — a practical demo of files, email, and collaboration habits.',
        },
        {
          name: 'Safe Account Setup Checklist',
          client: 'Practice use case: personal digital hygiene',
          value: 'Foundation / practice project',
          description:
            'Practice Gmail, passwords, two-step checks, downloads, and WhatsApp/Zoom basics so later tech courses start from a safer setup.',
        },
      ],
    },
    curriculum: [
      {
        title: 'Week 1: Computer Fundamentals',
        lessons: [
          { title: 'Parts of a computer: monitor, keyboard, mouse, CPU, storage', preview: true },
          { title: 'Turning on/off, login, desktop navigation', preview: true },
          { title: 'Files & folders: create, rename, move, delete, organize' },
          { title: 'Typing practice: English alphabet & Bangla (Avro Keyboard)' },
        ],
      },
      {
        title: 'Week 2: Internet, Browser & Email',
        lessons: [
          { title: 'How the internet works — Wi-Fi, mobile data, browsers' },
          { title: 'Google Chrome: tabs, bookmarks, downloads, history' },
          { title: 'Creating Gmail: compose, reply, attachments, spam awareness' },
          { title: 'Internet safety: strong passwords, phishing, privacy basics' },
        ],
      },
      {
        title: 'Week 3: Google Workspace & Cloud',
        lessons: [
          { title: 'Google Drive: upload, organize, share files & folders' },
          { title: 'Google Docs: create documents, formatting, print-ready' },
          { title: 'Google Sheets: data entry, basic formulas (SUM, AVERAGE)' },
          { title: 'Google Slides: create a simple 5-slide presentation' },
        ],
      },
      {
        title: 'Week 4: Essential English for Technology',
        lessons: [
          { title: 'Tech vocabulary: file, folder, download, upload, install, update, restart' },
          { title: 'Reading English menus, buttons & error messages on apps' },
          { title: 'Writing your name, introduction & simple emails in English' },
          { title: 'Understanding basic English instructions (YouTube tutorials)' },
        ],
      },
      {
        title: 'Week 5: Digital Communication & English Practice',
        lessons: [
          { title: 'WhatsApp & Zoom: messaging, calls, screen sharing' },
          { title: 'Writing professional messages in English (greeting, request, thank you)' },
          { title: 'Navigating English-interface apps: Facebook, YouTube, ChatGPT' },
          { title: 'Common tech phrases: "Click here", "Sign up", "Enter your email"' },
        ],
      },
      {
        title: 'Week 6: Practical Skills & Graduation',
        lessons: [
          { title: 'Installing apps, creating accounts, managing passwords' },
          { title: 'Screenshots, screen recording & file sharing (AirDrop, Drive, email)' },
          { title: 'Basic troubleshooting: slow internet, frozen screen, storage full' },
          { title: 'Final assessment: complete 10 real-world tasks independently' },
        ],
      },
    ],
  },
]
